import { useCallback, useEffect, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { isUserHasKey } from '../../GlobalFunctions';
import DistrictSelector from '../AdminReportComponents/DistrictSelector';
import SchoolSelector from '../AdminReportComponents/SchoolSelector';
import CustomDropdown from '../AdminReportComponents/CustomDropdown';
import { Icon } from '@iconify/react';
import { flushLocalForage, getLocalForage, setLocalForage } from '../../aaa';
import { useSelector } from 'react-redux';
import { RootState } from '../../ApplicationState';
import { SchoolModel, UserProfileType } from '@peekapak/types/DataProtocolTypes';
import {
  getClassrooms,
  getDistricts,
  getJob,
  getJournals as getJournalsFromDb,
  getSchools,
  getUsers,
  queryMixpanelByGroupV2,
} from '../../BackendInterface';
import { logger } from '../../error-tracker';
import Spinner from 'react-spinkit';
import SpinnerOverlay from '../../SpinnerOverlay';
import { getMixpanelDatabaseByDate, getStartOfSchoolYear, getStudentsToLoginsMap } from '../reportUtilities';
import {
  District,
  ReportingMixpanelEvent,
  SchoolsList,
  TeacherClassroomMap,
  TeacherClassroomMaps,
} from '@peekapak/types/ReportDataExchangeTypes';
import { PeekavilleJournalEntry } from '@peekapak/types/PeekavilleGameDataTypes';
import DateRangePicker from '../../components/DateRangePicker';
import ChartSegmentElementary from './ChartSegmentElementary';
import ChartSegmentSecondary from './ChartSegmentSecondary';
import styles from './MoodboardAdmin.module.scss';
import { getUpdatedSchoolUnifiedMoodScript } from './moodboardUtilities';
import LoadingBar from '../../components/LoadingBar';

interface MoodboardAdminState {
  legacyPlatformEvents: ReportingMixpanelEvent[];
  legacyPlatformEventsReady: boolean;
  legacyPeekavilleEvents: ReportingMixpanelEvent[];
  legacyPeekavilleEventsReady: boolean;
  newMixpanelEvents: ReportingMixpanelEvent[];
  newMixpanelEventsReady: boolean;
  disableChangeLocation?: boolean;
  districtsList: District[] | Record<string, unknown>[];
  districtsListReady: boolean;
  errorMessage: string;
  journalsMap: string[];
  journalsMapReady: boolean;
  journalsMapLoadProgress?: string;
  numOfJournalDataRetrived?: number;
  numOfStudentJournalData?: number;
  schoolsList: SchoolsList;
  selectedDistrict: string;
  schoolsListReady: boolean;
  selectedSchool: string;
  userList: {
    schoolId: string;
    list: UserProfileType[];
  };
  userListProgress: string;
  userListReady: boolean;
  teacherClassroomMaps: TeacherClassroomMaps;
  teacherClassroomMapsReady: boolean;
  selectedStartingDate: string;
  selectedEndingDate: string;
}

const tableOverrideSuffix = 'dev';

const MoodboardSchoolAdmin = (): JSX.Element => {
  const today = new Date();
  const startOfSchoolYear = getStartOfSchoolYear();

  const getCommonInitialState = () => {
    return {
      disableChangeLocation: true,
      errorMessage: '',
      journalsMap: [],
      journalsMapLoadProgress: '',
      journalsMapReady: false,
      legacyPeekavilleEvents: [],
      legacyPeekavilleEventsReady: false,
      legacyPlatformEvents: [],
      legacyPlatformEventsReady: false,
      newMixpanelEvents: [],
      newMixpanelEventsReady: false,
      numOfJournalDataRetrived: 0,
      numOfStudentJournalData: 0,
      userList: {
        list: [],
        schoolId: '',
      },
      userListProgress: '',
      userListReady: false,
      teacherClassroomMaps: {
        school: '',
        maps: [],
      },
      teacherClassroomMapsReady: false,
    };
  };

  const getDistrictInitialState = () => {
    return {
      districtsList: [],
      districtsListReady: false,
      selectedDistrict: '',
    };
  };

  const getSchoolInitialState = () => {
    return {
      schoolsList: {
        district: '',
        list: [],
      },
      schoolsListReady: false,
      selectedSchool: '',
    };
  };

  const getDateInitialState = () => {
    return {
      selectedStartingDate: startOfSchoolYear.toISOString().split('T')[0],
      selectedEndingDate: today.toISOString().split('T')[0],
    };
  };

  const [state, setState] = useState<MoodboardAdminState>({
    ...getCommonInitialState(),
    ...getDistrictInitialState(),
    ...getSchoolInitialState(),
    ...getDateInitialState(),
  });

  const [dateRange, setDateRange] = useState<DateRange>({ from: startOfSchoolYear, to: today });
  const [isDisplayDayPicker, setIsDisplayDayPicker] = useState(false);
  const [dateFilteredJournals, setDateFilteredJournals] = useState<ReportingMixpanelEvent[]>([]);
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [selectedClassroom, setSelectedClassroom] = useState('');

  const userProfile = useSelector((state: RootState) => state.user.userProfile);

  const handleBeforeStart = () => {};

  const teacherOptions =
    state.teacherClassroomMapsReady && state.teacherClassroomMaps?.maps
      ? [{ id: 'All', name: 'All' }, ...state.teacherClassroomMaps.maps.map((t) => ({ id: t.userId, name: t.name }))]
      : [{ id: 'All', name: 'All' }];

  const selectedTeacherObj =
    state.teacherClassroomMapsReady && selectedTeacher && selectedTeacher !== 'All'
      ? state.teacherClassroomMaps.maps.find((t) => t.userId === selectedTeacher)
      : null;

  const classroomOptions = selectedTeacherObj
    ? [
        { id: 'All', name: 'All' },
        ...selectedTeacherObj.classrooms.map((c) => ({ id: c.classroomId, name: c.className })),
      ]
    : [{ id: 'All', name: 'All' }];

  //
  // get list of districts
  //
  useEffect(() => {
    let mounted = true;
    run();

    return function cleanup() {
      mounted = false;
    };

    async function run() {
      if (!state.districtsListReady) {
        getListOfDistricts();
      } else {
        await restoreSavedData();
      }
    }
    async function getListOfDistricts() {
      const storageKey = 'districtsList';
      const map = await getLocalForage(storageKey);

      if (map && mounted) {
        return setState((prevState) => ({
          ...prevState,
          districtsList: map,
          districtsListReady: true,
        }));
      }

      const mapFromDb = await getDistricts();
      await setLocalForage(storageKey, mapFromDb, 1440);

      if (mounted) {
        setState((prevState) => ({
          ...prevState,
          districtsList: mapFromDb,
          districtsListReady: true,
        }));
      }
    }

    async function restoreSavedData() {
      const storageKey = 'selectedDistrict';
      const storageKeySchool = 'selectedSchool';
      const selectedDistrict = await getLocalForage(storageKey);
      const selectedSchool = await getLocalForage(storageKeySchool);

      if (selectedDistrict) {
        if (mounted) {
          if (selectedSchool) {
            setState((prevState) => ({
              ...prevState,
              selectedDistrict,
              selectedSchool,
            }));
          } else {
            setState((prevState) => ({ ...prevState, selectedDistrict }));
          }
        }
      }
    }
  }, [
    state.districtsList,
    state.districtsListReady,
    userProfile?.district,
    userProfile?.schoolId,
    state.selectedDistrict,
  ]);
  //
  // schools in district
  //
  useEffect(() => {
    let mounted = true;
    if (!state.schoolsListReady) getListOfSchoolsInDistrict();
    return function cleanup() {
      mounted = false;
    };

    async function getListOfSchoolsInDistrict() {
      const districtStorageKey = 'selectedDistrict';
      const district =
        state.selectedDistrict || (await getLocalForage(districtStorageKey)) || userProfile?.district || '';
      if (!district) return;
      const storageKey = `${district}-schoolsList`;
      const cachedList = await getLocalForage(storageKey);
      const list = cachedList?.filter((s: SchoolModel) => !s.isArchived);

      if (list && mounted) {
        return setState((prevState) => ({
          ...prevState,
          schoolsList: {
            district,
            list,
          },
          schoolsListReady: true,
        }));
      }

      const listFromDb = (await getSchools(district, tableOverrideSuffix))?.filter((s) => !s.isArchived);
      await setLocalForage(storageKey, listFromDb, 43200);

      if (mounted) {
        setState((prevState) => ({
          ...prevState,
          schoolsList: {
            district,
            list: listFromDb,
          },
          schoolsListReady: true,
        }));
      }
    }
  }, [userProfile, state.schoolsListReady, state.selectedDistrict, state.districtsListReady]);
  //
  // get list of users in selected school
  //
  useEffect(() => {
    let mounted = true;
    run();

    return function cleanup() {
      mounted = false;
    };

    async function run() {
      if (state.selectedSchool) {
        const fromStorageForAll = await getLocalForage(`${state.selectedSchool}-users`);

        if (fromStorageForAll && mounted) {
          setState((prevState) => ({
            ...prevState,
            userList: {
              list: fromStorageForAll,
              schoolId: state.selectedSchool,
            },
            userListReady: true,
          }));
        } else {
          getUsersInSchool();
        }
      }
    }
    async function getUsersInSchool() {
      const jobId = await getUsers(state.selectedSchool, tableOverrideSuffix);
      let job: {
        status: string;
        data: string;
      } = await getJob(jobId);

      try {
        while (isJobPending(job)) {
          processPendingJob(job);
          await yieldAndWait();
          job = await getJob(jobId);
        }
      } catch (e) {
        const err = e as Error;
        setState((prevState) => ({
          ...prevState,
          errorMessage: err.message,
        }));
        logger.logException(err);

        throw err;
      }

      const data = JSON.parse(job.data);

      if (job.status === 'Done') {
        await setLocalForage(`${state.selectedSchool}-users`, data, 10080);

        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            userList: {
              list: data,
              schoolId: state.selectedSchool,
            },
            userListReady: true,
          }));
        }
      } else if (job.status === 'Error') {
        if (mounted) {
          if (job.data) {
            const data = JSON.parse(job.data);
            setState((prevState) => ({
              ...prevState,
              errorMessage: data.message,
            }));
            logger.logException(new Error(data.message));
          } else {
            const e = new Error('Unknown error occurred while trying to retrieve list of users');
            setState((prevState) => ({
              ...prevState,
              errorMessage: e.message,
            }));
            logger.logException(e);
          }
        }
      } else {
        throw new Error(`Unexpected job status ${job.status}`);
      }
    }
    function isJobPending(job: { status: string }) {
      return job.status !== 'Done' && job.status !== 'Error';
    }

    function processPendingJob(job: { data: string }) {
      if (job.data) {
        const data = JSON.parse(job.data);

        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            userListProgress: `Found ${data.numberOfResults} users`,
          }));
        }
      }
    }

    function yieldAndWait() {
      return new Promise<void>((resolve) => {
        setTimeout(() => resolve(), 1000);
      });
    }
  }, [state.selectedSchool, state.selectedStartingDate, state.selectedEndingDate]);

  //
  // set selected district
  //
  useEffect(() => {
    run();
    async function run() {
      if (state.districtsListReady && state.districtsList.length > 0) {
        const storageKey = 'selectedDistrict';
        const selectedDistrict = await getLocalForage(storageKey);

        if (selectedDistrict) {
          setState((prevState) => ({ ...prevState, selectedDistrict }));
        } else {
          if (userProfile?.district) {
            if (state.districtsList.map((d) => d.id).includes(userProfile.district)) {
              return setState((prevState) => ({
                ...prevState,
                selectedDistrict: userProfile.district!,
              }));
            }
          }
        }
        return;
      }
      setState((prevState) => ({ ...prevState, selectedDistrict: '' }));
    }
  }, [userProfile?.district, state.districtsListReady, state.districtsList]);

  //
  // set selected school
  //
  useEffect(() => {
    run();
    async function run() {
      if (state.selectedDistrict && state.schoolsListReady && state.schoolsList.list.length > 0) {
        const district = state.selectedDistrict || userProfile?.district;
        const storageKey = `${district}-selectedSchool`;
        const selectedSchool = await (async () => {
          if (!userProfile?.district) {
            return '';
          }

          if (isUserHasKey('KEY_ADMINISTRATOR_REPORTS_SELECT_SCHOOL')) {
            return (await getLocalForage(storageKey)) || state.schoolsList.list[0].id;
          }

          return (await getLocalForage(storageKey)) || userProfile.schoolId || '';
        })();

        setState((prevState) => ({ ...prevState, selectedSchool }));

        return;
      }

      setState((prevState) => ({ ...prevState, selectedSchool: '' }));
    }
  }, [
    state.schoolsListReady,
    state.schoolsList.list,
    state.selectedDistrict,
    userProfile?.district,
    userProfile?.schoolId,
  ]);

  //
  // get teacher-classroom maps for selected school
  //
  useEffect(() => {
    let mounted = true;

    if (state.userListReady && !state.teacherClassroomMapsReady) {
      getTeacherClassroomMaps();
    }

    return function cleanup() {
      mounted = false;
    };

    async function getTeacherClassroomMaps() {
      const storageKey = `${state.selectedSchool}-teacherClassroomMaps`;
      const cached = await getLocalForage(storageKey);

      if (cached && mounted) {
        return setState((prevState) => ({
          ...prevState,
          teacherClassroomMaps: JSON.parse(cached as string),
          teacherClassroomMapsReady: true,
        }));
      }

      const teachers = state.userList.list.filter(
        (u) => u.type === 'Teacher' || u.type === 'SchoolAdmin' || u.type === 'DistrictAdmin',
      );
      const teacherClassroomMapList: TeacherClassroomMap[] = [];
      let i = 0;

      while (i < teachers.length) {
        const classrooms = await getClassrooms(teachers[i].email, tableOverrideSuffix);
        teacherClassroomMapList.push({
          name: `${teachers[i].firstName} ${teachers[i].lastName}`,
          email: teachers[i].email,
          userId: teachers[i].userId,
          classrooms,
        });
        i += 1;
      }

      teacherClassroomMapList.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));

      const maps: TeacherClassroomMaps = {
        school: state.selectedSchool,
        maps: teacherClassroomMapList,
      };

      await setLocalForage(storageKey, JSON.stringify(maps), 60);

      if (mounted) {
        setState((prevState) => ({
          ...prevState,
          teacherClassroomMaps: maps,
          teacherClassroomMapsReady: true,
        }));
      }
    }
  }, [state.userListReady, state.userList.list, state.selectedSchool, state.teacherClassroomMapsReady]);

  //
  // get legacy platform events for this school
  // Legacy Mixpanel databases are no longer queried - just set empty array immediately
  //
  useEffect(() => {
    let mounted = true;

    if (state.selectedSchool && state.schoolsListReady && !state.legacyPlatformEventsReady) {
      if (mounted) {
        setState((prevState) => ({
          ...prevState,
          legacyPlatformEvents: [],
          legacyPlatformEventsReady: true,
        }));
      }
    }

    return function cleanup() {
      mounted = false;
    };
  }, [state.selectedSchool, state.schoolsListReady, state.legacyPlatformEventsReady]);

  //
  // get legacy Peekaville events for this school
  // Legacy Mixpanel databases are no longer queried - just set empty array immediately
  //
  useEffect(() => {
    let mounted = true;

    if (state.schoolsListReady && state.selectedSchool && !state.legacyPeekavilleEventsReady) {
      if (mounted) {
        setState((prevState) => ({
          ...prevState,
          legacyPeekavilleEvents: [],
          legacyPeekavilleEventsReady: true,
        }));
      }
    }

    return function cleanup() {
      mounted = false;
    };
  }, [state.selectedSchool, state.schoolsListReady, state.legacyPeekavilleEventsReady]);

  //get new mixpanel DB analytics data

  useEffect(() => {
    let mounted = true;
    run();

    return function cleanup() {
      mounted = false;
    };

    async function run() {
      const selectedStartingDate = state.selectedStartingDate;
      const selectedEndingDate = state.selectedEndingDate;

      const storageKey = `${state.selectedSchool}-newMixpanelEvents-from-${selectedStartingDate}-to-${selectedEndingDate}`;

      if (state.selectedSchool && state.schoolsListReady) {
        const data = await getLocalForage(storageKey);
        if (!data) {
          doQuery();
        } else {
          if (mounted) {
            setState((prevState) => ({
              ...prevState,
              newMixpanelEvents: data,
              newMixpanelEventsReady: true,
            }));
          }
        }
      }

      async function doQuery() {
        const selectedSchoolObject = state.schoolsList.list.find((s) => s.id === state.selectedSchool);

        if (!selectedSchoolObject)
          throw new Error(
            `Unexpected condition: could not find a matching school object for selected school in report`,
          );

        try {
          const result = await queryMixpanelByGroupV2(
            getUpdatedSchoolUnifiedMoodScript(
              selectedStartingDate,
              selectedEndingDate,
              selectedSchoolObject.combineIds || selectedSchoolObject.id,
            ),
            'unified',
            tableOverrideSuffix,
          );

          await setLocalForage(storageKey, result, 60);

          if (mounted) {
            setState((prevState) => ({
              ...prevState,
              newMixpanelEvents: result,
              newMixpanelEventsReady: true,
            }));
          }
        } catch (error) {
          if (mounted) {
            setState((prevState) => ({
              ...prevState,
              errorMessage: (error as Error).message,
            }));
          }
        }
      }
    }
  }, [state.selectedSchool, state.selectedStartingDate, state.selectedEndingDate]);

  //
  // get students' journal entries
  //
  useEffect(() => {
    let mounted = true;

    const selectedStartingDate = state.selectedStartingDate;
    const selectedEndingDate = state.selectedEndingDate;

    if (
      state.userListReady &&
      state.selectedSchool &&
      state.legacyPeekavilleEventsReady &&
      state.newMixpanelEventsReady &&
      !state.journalsMapReady
    ) {
      getJournals();
    }

    return function cleanup() {
      mounted = false;
    };

    async function getJournals() {
      const storageKey = `${state.selectedSchool}-journals-from-${selectedStartingDate}-to-${selectedEndingDate}`;
      const map = await getLocalForage(storageKey);

      if (map && mounted) {
        return setState((prevState) => ({
          ...prevState,
          journalsMap: map,
          journalsMapReady: true,
        }));
      }

      const DBtoQuery = getMixpanelDatabaseByDate(dateRange.from as Date, dateRange.to as Date);
      if (DBtoQuery === 'newDB') {
        await setLocalForage(storageKey, state.newMixpanelEvents, 1440);
        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            journalsMap: state.newMixpanelEvents as unknown as string[],
            journalsMapReady: true,
          }));
        }
      } else if (DBtoQuery === 'legacyDB') {
        const studentIdToLoginsMap = getStudentsToLoginsMap([
          ...state.legacyPeekavilleEvents,
          ...state.legacyPlatformEvents,
        ]);
        const students = state.userList.list.filter(
          (u) => u.type === 'Student' && studentIdToLoginsMap[u.userId as keyof typeof studentIdToLoginsMap],
        );
        const journalsArray: PeekavilleJournalEntry[] = [];
        let i = 0;
        while (i < students.length) {
          const journals = await getJournalsFromDb(students[i].userId, tableOverrideSuffix);

          journalsArray.push(...Object.values(journals));
          if (mounted) {
            setState((prevState) => ({
              ...prevState,
              journalsMapLoadProgress: `${i + 1} of ${students.length}`,
              numOfStudentJournalData: students.length,
              numOfJournalDataRetrived: i + 1,
            }));
          }

          i += 1;
        }

        await setLocalForage(storageKey, journalsArray, 1440);

        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            journalsMap: journalsArray as unknown as string[],
            journalsMapReady: true,
          }));
        }
      } else if (DBtoQuery === 'mixed') {
        const studentIdToLoginsMap = getStudentsToLoginsMap([
          ...state.legacyPeekavilleEvents,
          ...state.legacyPlatformEvents,
        ]);
        const students = state.userList.list.filter(
          (u) => u.type === 'Student' && studentIdToLoginsMap[u.userId as keyof typeof studentIdToLoginsMap],
        );
        let i = 0;
        const journalsArray: PeekavilleJournalEntry[] = [];

        while (i < students.length) {
          const journals = await getJournalsFromDb(students[i].userId, tableOverrideSuffix);
          journalsArray.push(...Object.values(journals));
          if (mounted) {
            setState((prevState) => ({
              ...prevState,
              journalsMapLoadProgress: `${i + 1} of ${students.length}`,
              numOfStudentJournalData: students.length,
              numOfJournalDataRetrived: i + 1,
            }));
          }

          i += 1;
        }
        const combinedJournalsMap = [...journalsArray, ...state.newMixpanelEvents];
        await setLocalForage(storageKey, combinedJournalsMap, 1440);

        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            journalsMap: combinedJournalsMap as unknown as string[],
            journalsMapReady: true,
          }));
        }
      }
    }
  }, [
    state.legacyPeekavilleEvents,
    state.legacyPeekavilleEventsReady,
    state.newMixpanelEvents,
    state.newMixpanelEventsReady,
    state.userList.schoolId,
    state.selectedSchool,
    state.journalsMapReady,
    state.userListReady,
    state.userList.list,
    state.selectedStartingDate,
    state.selectedEndingDate,
  ]);

  //
  // disable drop downs when data is being retrieved
  //
  useEffect(() => {
    if (
      (!state.districtsListReady ||
        !state.schoolsListReady ||
        !state.userListReady ||
        !state.legacyPlatformEventsReady ||
        !state.legacyPeekavilleEventsReady ||
        !state.newMixpanelEventsReady ||
        !state.journalsMapReady) &&
      !state.errorMessage
    ) {
      return setState((prevState) => ({
        ...prevState,
        disableChangeLocation: true,
      }));
    }

    return setState((prevState) => ({
      ...prevState,
      disableChangeLocation: false,
    }));
  }, [
    state.districtsListReady,
    state.schoolsListReady,
    state.userListReady,
    state.legacyPlatformEventsReady,
    state.legacyPeekavilleEventsReady,
    state.newMixpanelEventsReady,
    state.journalsMapReady,
    state.errorMessage,
  ]);

  //
  // set error conditions
  //
  useEffect(() => {
    if (userProfile && !userProfile.district && !isUserHasKey('KEY_PEEKAPAK_MANAGEMENT')) {
      setState((prevState) => ({
        ...prevState,
        disableChangeLocation: false,
        errorMessage: 'No data available',
      }));
    }

    if (userProfile?.district && !isUserHasKey('KEY_ADMINISTRATOR_REPORTS_SELECT_SCHOOL') && !userProfile.schoolId) {
      setState((prevState) => ({
        ...prevState,
        disableChangeLocation: false,
        errorMessage: 'No data available',
      }));
    }

    if (state.schoolsListReady && state.schoolsList.list.length === 0) {
      setState((prevState) => ({
        ...prevState,
        disableChangeLocation: false,
        errorMessage: 'No school data applicable',
      }));
    }
  }, [userProfile, state.schoolsListReady, state.schoolsList]);

  const getClassroomIdsForTeacher = (teacherId: string): string[] => {
    const teacher = state.teacherClassroomMaps.maps.find((t) => t.userId === teacherId);
    if (!teacher) return [];
    return teacher.classrooms.map((c) => c.classroomId);
  };

  const filterDataByDateAndTeacher = useCallback(() => {
    if (state.journalsMapReady) {
      const journals = (state.journalsMap as unknown as Record<string, unknown>[]).filter(
        (e: Record<string, unknown>) => {
          const createdAt = e.createdAt as string | undefined;
          if (!createdAt || !dateRange.from || !dateRange.to) return false;
          const d = new Date(createdAt);
          return d >= dateRange.from && d <= dateRange.to;
        },
      ) as unknown as ReportingMixpanelEvent[];

      const classroomIds =
        selectedTeacher && selectedTeacher !== 'All'
          ? selectedClassroom && selectedClassroom !== 'All'
            ? [selectedClassroom]
            : getClassroomIdsForTeacher(selectedTeacher)
          : null;

      const filtered = classroomIds
        ? journals.filter((e: Record<string, unknown>) => classroomIds.includes(e.classroomId as string))
        : journals;

      setDateFilteredJournals(filtered as ReportingMixpanelEvent[]);
    }
  }, [
    dateRange,
    state.journalsMap,
    state.journalsMapReady,
    selectedTeacher,
    selectedClassroom,
    state.teacherClassroomMaps.maps,
  ]);

  useEffect(() => {
    filterDataByDateAndTeacher();
  }, [dateRange, state.journalsMap, state.journalsMapReady, selectedTeacher, selectedClassroom]);

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      ...getCommonInitialState(),
      ...getDistrictInitialState(),
      ...getSchoolInitialState(),
    }));
    setSelectedTeacher('');
    setSelectedClassroom('');
  }, [state.selectedStartingDate, state.selectedEndingDate]);

  async function onSelectDistrict(eventKey: string | null) {
    if (state.districtsListReady && state.districtsList.length > 0) {
      const selectedDistrict = eventKey || '';
      setState((prevState) => ({
        ...prevState,
        ...getCommonInitialState(),
        ...getSchoolInitialState(),
        selectedDistrict,
      }));
      const storageKey = 'selectedDistrict';
      await setLocalForage(storageKey, selectedDistrict);
    } else {
      console.warn(`%cDistrict List is Missing`, 'background: black; color:yellow');
    }
  }

  async function onSelectSchool(eventKey: string) {
    if (state.schoolsListReady && state.schoolsList.list.length > 0) {
      const district = state.selectedDistrict || userProfile?.district;
      const selectedSchool = eventKey;

      setState((prevState) => ({
        ...prevState,
        ...getCommonInitialState(),
        ...getSchoolInitialState(),
        selectedSchool,
      }));
      const storageKey = `${district}-selectedSchool`;
      await setLocalForage(storageKey, selectedSchool);
    } else {
      console.warn(`%cSchool List is Missing`, 'background: black; color:yellow');
    }
  }

  async function onReload() {
    await flushLocalForage();
    setState((prevState) => ({
      ...prevState,
      ...getCommonInitialState(),
      ...getDateInitialState(),
      ...getDistrictInitialState(),
    }));
  }

  const getElementaryData = () => {
    return dateFilteredJournals.filter((e: Record<string, unknown>) => parseInt(e.grade as string, 10) <= 5);
  };

  const getSecondaryData = () => {
    return dateFilteredJournals.filter((e: Record<string, unknown>) => parseInt(e.grade as string, 10) >= 6);
  };

  if (!userProfile || !state.schoolsListReady) {
    return <SpinnerOverlay isShow />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.centeringContainer}>
        <div className={styles.districtAndSchoolSelectors}>
          <div className={styles.titleAndReloadButton}>
            <h1>School Wide Mood Check-in</h1>
            <Icon className={styles.reloadIcon} icon='zondicons:reload' onClick={onReload} />
          </div>
          <DistrictSelector
            isPeekapakAdmin={!!isUserHasKey('KEY_PEEKAPAK_MANAGEMENT')}
            districtsList={state.districtsList}
            onReload={onReload}
            onSelectDistrict={onSelectDistrict}
            initialValue={state.selectedDistrict}
            currentlySelectedDistrict={state.selectedDistrict}
            disableChangeLocation={state.disableChangeLocation || false}
          />
          <SchoolSelector
            isDistrictAdmin={!!isUserHasKey('KEY_ADMINISTRATOR_REPORTS_SELECT_SCHOOL')}
            schoolsList={state.schoolsList}
            onSelectSchool={onSelectSchool}
            initialValue={state.selectedSchool}
            currentlySelectedSchool={state.selectedSchool}
            disableChangeLocation={state.disableChangeLocation || false}
          />
        </div>
        <div>
          <DateRangePicker
            disabled={state.disableChangeLocation || false}
            dateRange={dateRange}
            setDateRange={setDateRange}
            startDate={startOfSchoolYear}
            excludeAfter={today}
            beforeFirstInputText='Start Date:'
            beforeSecondInputText='End Date:'
            isDisplayCalendar={isDisplayDayPicker}
            setIsDisplayCalendar={setIsDisplayDayPicker}
            handleBeforeStart={handleBeforeStart}
          />
        </div>
        {!state.legacyPeekavilleEventsReady ||
        !state.legacyPlatformEventsReady ||
        !state.newMixpanelEventsReady ||
        !dateFilteredJournals ||
        (!state.journalsMapReady && state.journalsMapLoadProgress === '') ? (
          <div className={styles.loadingContainer}>
            <Spinner />
          </div>
        ) : !state.journalsMapReady && state.journalsMapLoadProgress !== '' ? (
          <div className={styles.loadingContainer}>
            <LoadingBar
              title={'Loading...'}
              width={500}
              totalNum={state.numOfStudentJournalData || 0}
              numComplete={state.numOfJournalDataRetrived || 0}
              barColor='#012a73'
            />
          </div>
        ) : (
          <div className={styles.chartsContainer}>
            <div className={styles.selectorLine}>
              <CustomDropdown
                list={teacherOptions}
                onSelectSubject={(value) => {
                  setSelectedTeacher(value || '');
                  setSelectedClassroom('');
                }}
                initialValue={teacherOptions[0].id}
                currentlySelectedItem={selectedTeacher}
                disableChangeLocation={false}
                icon='ph:chalkboard-teacher'
                placeholder='Select a teacher'
              />
              <CustomDropdown
                list={classroomOptions}
                onSelectSubject={(value) => setSelectedClassroom(value || '')}
                initialValue={classroomOptions[0].id}
                currentlySelectedItem={selectedClassroom}
                disableChangeLocation={false}
                icon='icon-park-outline:classroom'
                placeholder='Select a classroom'
              />
            </div>
            <ChartSegmentElementary
              data={getElementaryData()}
              dataAvailabilityMessage=''
              previousPeriodData={[]}
              dateRange={{ from: dateRange.from || startOfSchoolYear, to: dateRange.to || today }}
              selectedPeriod='yearly'
              shiftPeriod={() => {}}
              shouldShowNavArrowLeft={() => false}
              shouldShowNavArrowRight={() => false}
            />
            <ChartSegmentSecondary
              data={getSecondaryData()}
              dataAvailabilityMessage=''
              previousPeriodData={[]}
              dateRange={{ from: dateRange.from || startOfSchoolYear, to: dateRange.to || today }}
              selectedPeriod='yearly'
              shiftPeriod={() => {}}
              shouldShowNavArrowLeft={() => false}
              shouldShowNavArrowRight={() => false}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MoodboardSchoolAdmin;
